/**
 * columnBuilder.ts
 * ────────────────
 * Helper pur extrait de {@code useDataTable.setInitialVariables} . Construit
 * la liste des {@code Component} d'un datatype à partir des componentKeys
 * filtrables ( {@code variables} ) + de la configuration de l'application .
 *
 * <p><b>Pourquoi extraire</b> :
 * <ul>
 *   <li>Permet d'être appelé depuis 2 sites : {@code setInitialVariables}
 *       ( post-{@code /data} ) ET {@code loadFiltersAsync.then} ( post-
 *       {@code /filters} ) → bloc filtre peut s'afficher dès l'arrivée
 *       de {@code /filters} sans attendre {@code /data} ;</li>
 *   <li>SRP : la construction des Component est une opération pure
 *       ( config + variables → Component[] ) , isolée de la gestion
 *       de l'état Vue ;</li>
 *   <li>Testabilité : fonction pure , 0 dépendance Vue / reactive , 100%
 *       unit-testable en isolation .</li>
 * </ul>
 *
 * <p><b>Iso-comportement</b> : la logique de construction est strictement
 * identique à celle de {@code setInitialVariables} pré-refacto . Les
 * Component instances produites sont assignables directement à
 * {@code columns.value} sans transformation supplémentaire .
 *
 * <p><b>NB</b> : la partie async ( chargement DynamicComponent referenceType )
 * n'est PAS dans ce helper - elle reste dans {@code setInitialVariables}
 * car elle dépend de {@code application.data?.[dataId]} qui n'est
 * disponible qu'après {@code /data} .
 */
import { Component } from "@/types/front/LocalComponent";
import { LocalApplicationResult } from "@/types/front/LocalApplicationResult";

interface ColumnBuildServices {
  internationalisationService: {
    localeReferenceColumnsNames: (referenceName: string, headerKey: string, application: LocalApplicationResult) => string;
    getLocaleforPath: (application: LocalApplicationResult, path: string, fallback: string) => string;
  };
}

export interface ColumnBuildResult {
  /** Component pour les champs basiques ( BasicComponent , ReferenceChecker , etc. ) . */
  components: Record<string, Component>;
  /** Component pour les DynamicComponent ( référence dynamique - chargement async séparé ) . */
  dynamicComponents: Record<string, Component>;
}

/**
 * Construit les Component d'un datatype depuis les variables filtrables .
 *
 * @param application Application courante ( fournit la config YAML )
 * @param dataId      nom du datatype
 * @param variables   set des componentKeys filtrables à inclure
 * @param services    services Vue pour i18n / locale ( injecté pour testabilité )
 * @returns map { components , dynamicComponents } prêts à être triés et
 *          fusionnés via {@link mergeAndSortColumns}
 */
export function buildColumnsFromVariables(
  application: LocalApplicationResult,
  dataId: string,
  variables: Set<string>,
  services: ColumnBuildServices,
): ColumnBuildResult {
  const components: Record<string, Component> = {};
  const dynamicComponents: Record<string, Component> = {};

  const dataDescription = application?.configuration?.dataDescription?.[dataId];
  if (!dataDescription) return { components, dynamicComponents };

  const componentDescriptions: Record<string, any> = (dataDescription as any).componentDescriptions || {};
  if (variables.size === 0) return { components, dynamicComponents };

  const buildConfig = {
    referenceName: dataId,
    application,
    getIntl: services.internationalisationService.localeReferenceColumnsNames,
    intl: (tagName: string) =>
      services.internationalisationService.getLocaleforPath(application, "tags." + tagName, tagName),
  };

  for (const componentKey in componentDescriptions) {
    if (!variables.has(componentKey)) continue;
    const raw = componentDescriptions[componentKey];
    if (raw.type === "DynamicComponent") {
      dynamicComponents[componentKey] = Component.build(raw, buildConfig);
    }
    else if (raw.type !== "AuthorizationScopeComponent" && raw.type !== "PatternComponentComponent") {
      components[componentKey] = Component.build(raw, buildConfig);
    }
  }

  return { components, dynamicComponents };
}

/**
 * Fusionne basiques + dynamiques triés via {@link Component.compare} ,
 * dans l'ordre attendu par le rendu UI ( basiques en premier , dynamiques
 * ensuite , chaque groupe trié alphabétiquement par
 * {@code componentKey} ) .
 */
export function mergeAndSortColumns(result: ColumnBuildResult): Component[] {
  return [
    ...Object.values(result.components).sort(Component.compare),
    ...Object.values(result.dynamicComponents).sort(Component.compare),
  ];
}
